import './JobsList.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faArrowAltCircleUp, faDatabase, faDownload, faEdit, faPlay, faStop } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { ReactElement, useState } from 'react';
import { ProgressBar } from '../../../../__legacy__/components/ProgressBar';
import { GTable, IGColumnProps } from '../../../../__legacy__/components/table/GTable';
import { EnableFor } from '../../../../__legacy__/containers/EnableForRole';
import { formatSecondsToTime, toLocaleDateTimeString } from '../../../../__legacy__/helpers/date.helpers';
import { downloadFileWithCheck } from '../../../../__legacy__/helpers/fileDownload.helpers';
import { S_TabsCard } from '../../../../design/card/tabsCard/S_TabsCards';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { S_TableClipLoader } from '../../../../design/loaders/clipLoader/S_TableClipLoader';
import { S_PaginationProps } from '../../../../design/pagination/S_Pagination';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { JobStatusBadge } from '../../../common/statusesBadges/modelStatus/JobStatusBadge';
import { useJobPermissions } from '../../jobPermissions';
import { IInferenceListItem, IJobDownload, IJobListItem, ITrainingListItem, JOB_STATUS, JOB_TYPE, TRAINING_TYPE } from '../../jobs.model';
import { getJobRoutePrefix, getJobTranslationPrefix } from '../../utils';
import { JobDownloadModal } from '../JobDownloadModal';

library.add(faArrowAltCircleUp, faDownload, faPlay, faStop, faEdit);

interface IJobsListTableProps {
  workspaceId: string;
  jobType: JOB_TYPE;
  jobs: IJobListItem[];
  isLoading: boolean;
  sorting: {
    orderBy: string;
    orderType: string;
  };
  pagination: S_PaginationProps;
  downloadJobAsync: (jobId: string) => Promise<IJobDownload | null>;
  onRestartJob: (jobItem: IJobListItem) => Promise<void>;
  onStopJob: (jobItem: IJobListItem) => Promise<void>;
  onDeleteJob: (jobItem: IJobListItem) => Promise<void>;
  onEditJob: (jobItem: IJobListItem) => void;
  onOrderingChange: (orderBy: string, orderType: string) => void;
}

export const JobsList = ({
  workspaceId,
  jobType,
  jobs,
  isLoading,
  sorting,
  pagination,
  downloadJobAsync,
  onRestartJob,
  onStopJob,
  onDeleteJob,
  onEditJob,
  onOrderingChange,
}: IJobsListTableProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useJobPermissions();
  const translationPrefix = getJobTranslationPrefix(jobType);
  const [downloadData, setDownloadData] = useState<IJobDownload | null>(null);

  const initializeDownloadAsync = async (jobItem: IJobListItem): Promise<void> => {
    const downloadResult = await downloadJobAsync(jobItem.id);
    setDownloadData(downloadResult);
  };

  const submitDownload = async (): Promise<void> => {
    if (!downloadData) {
      return;
    }

    const downloadUrl = await downloadData.urlGenerator();
    if (downloadUrl) {
      await downloadFileWithCheck(downloadUrl);
    }

    setDownloadData(null);
  };

  const cancelDownload = (): void => {
    setDownloadData(null);
  };

  const getProgressBar = (startingProgress: IJobListItem['startingProgress']): ReactElement => {
    let progress = 0;
    switch (startingProgress) {
      case '1/4':
        progress = 25;
        break;
      case '2/4':
        progress = 50;
        break;
      case '3/4':
        progress = 75;
        break;
      case undefined:
      case null:
        break;
    }

    return (
      <ProgressBar
        value={progress}
        enableStripesAnimation
      />
    );
  };

  const renderStatus = (jobItem: IJobListItem): ReactElement => {
    const { status, failureReason, startingProgress, jobType } = jobItem;

    if (
      jobType === JOB_TYPE.TRAINING &&
      jobItem.trainingType === TRAINING_TYPE.AUTO &&
      status === JOB_STATUS.STARTING &&
      (startingProgress === '2/4' || startingProgress === '3/4')
    ) {
      return (
        <div className='status-with-progress'>
          <JobStatusBadge jobStatus={JOB_STATUS.RUNNING} />
        </div>
      );
    }

    return (
      <div className='status-with-progress'>
        <JobStatusBadge
          jobStatus={status}
          failureReason={failureReason ?? undefined}
        />
        {status === JOB_STATUS.STARTING && getProgressBar(startingProgress)}
      </div>
    );
  };

  const renderName = (jobItem: IJobListItem): ReactElement => {
    const { status, id } = jobItem;
    const metricStatuses = [
      JOB_STATUS.FINISHING,
      JOB_STATUS.STARTING,
      JOB_STATUS.RUNNING,
      JOB_STATUS.EVALUATING,
      JOB_STATUS.FINISHED,
      JOB_STATUS.IMPORTED,
      JOB_STATUS.IMPORTING,
      JOB_STATUS.WAITING,
    ];

    const defaultLink = jobType === JOB_TYPE.TRAINING ? getJobRoutePrefix(jobType).Details.Settings : getJobRoutePrefix(jobType).Details.Datasets;
    const link = metricStatuses.includes(status)
      ? getJobRoutePrefix(jobType).Details.Metrics.withParams({ workspaceId, jobId: id })
      : defaultLink.withParams({ workspaceId, jobId: id });

    return (
      <S_Link2
        color='text'
        to={link}
        className='link-flex'
      >
        {jobItem.jobType === JOB_TYPE.TRAINING && jobItem.trainingType === TRAINING_TYPE.AUTO && (
          <FontAwesomeIcon
            className='link-icon'
            icon={faDatabase}
          />
        )}
        {jobItem.jobType === JOB_TYPE.TRAINING ? jobItem.trainingName : jobItem.inferenceName}
      </S_Link2>
    );
  };

  const renderVariant = (jobItem: IJobListItem): ReactElement => {
    return <>{t(`training_variants.${jobItem.trainingVariant}`)}</>;
  };

  const renderCreationDate = (jobItem: IJobListItem): ReactElement => <>{toLocaleDateTimeString(jobItem.date)}</>;

  const renderProject = (jobItem: IJobListItem): ReactElement => <>{jobItem.projectName ? jobItem.projectName : t('na')}</>;

  const renderTrainingTime = (jobItem: ITrainingListItem): ReactElement => {
    if ([JOB_STATUS.IMPORTED, JOB_STATUS.IMPORTING].includes(jobItem.status) || jobItem.trainingTime === 0) {
      return <>{t('na')}</>;
    }
    return <>{formatSecondsToTime(jobItem.trainingTime)}</>;
  };

  const renderProgress = (jobItem: ITrainingListItem): ReactElement => <>{jobItem.progress === '0' ? t('na') : jobItem.progress}</>;

  const renderScore = (jobItem: IJobListItem): ReactElement => <>{jobItem.score === null ? t('na') : jobItem.score.toFixed(3)}</>;

  const renderCost = (jobItem: IJobListItem): ReactElement => <>{!jobItem.costCalculated ? t('na') : `${Math.ceil(jobItem.costCalculated * 100)}`}</>;

  const renderActions = (jobItem: IJobListItem): ReactElement => {
    const progressStatuses = [JOB_STATUS.STARTING, JOB_STATUS.FAILING, JOB_STATUS.STOPPING, JOB_STATUS.IMPORTING, JOB_STATUS.SAVING, JOB_STATUS.FINISHING];
    const stoppableStatuses = [JOB_STATUS.RUNNING, JOB_STATUS.EVALUATING, JOB_STATUS.WAITING];
    return (
      <>
        {progressStatuses.includes(jobItem.status) && <S_TableClipLoader />}
        {stoppableStatuses.includes(jobItem.status) && (
          <EnableFor hasPermission={permissions.canUpdate(jobItem.isOwner)}>
            <IconButton
              size='sm'
              color='primary'
              onClick={(): void => {
                void onStopJob(jobItem);
              }}
              title={t(`${translationPrefix}.stop.text`)}
            >
              <FontAwesomeIcon icon={faStop} />
            </IconButton>
          </EnableFor>
        )}
        {jobItem.status === JOB_STATUS.STOPPED && (
          <EnableFor hasPermission={permissions.canRun()}>
            <IconButton
              size='sm'
              color='primary'
              onClick={(): void => {
                void onRestartJob(jobItem);
              }}
              title={t(`${translationPrefix}.start.text`)}
            >
              <FontAwesomeIcon icon={faPlay} />
            </IconButton>
          </EnableFor>
        )}
        {jobItem.status === JOB_STATUS.FAILED && (
          <EnableFor hasPermission={permissions.canRun()}>
            <IconButton
              size='sm'
              color='primary'
              onClick={(): void => {
                onEditJob(jobItem);
              }}
              title={t(`${translationPrefix}.edit.button`)}
            >
              <FontAwesomeIcon icon={faEdit} />
            </IconButton>
          </EnableFor>
        )}
        {(jobItem.status === JOB_STATUS.FINISHED || jobItem.status === JOB_STATUS.IMPORTED) && (
          <EnableFor hasPermission={permissions.canDownload(jobItem.jobType === JOB_TYPE.TRAINING ? jobItem.isOwner : jobItem.isTrainingOwner)}>
            <IconButton
              size='sm'
              color='primary'
              onClick={() => void initializeDownloadAsync(jobItem)}
              title={t('download.title_model')}
            >
              <FontAwesomeIcon icon={faDownload} />
            </IconButton>
          </EnableFor>
        )}
        <EnableFor hasPermission={permissions.canDelete(jobItem.isOwner)}>
          <IconButton
            size='sm'
            onClick={(): void => {
              void onDeleteJob(jobItem);
            }}
            title={t(`${translationPrefix}.delete.text`)}
          >
            <TrashIcon />
          </IconButton>
        </EnableFor>
      </>
    );
  };

  const getColumns = <T extends JOB_TYPE>(jobType: T): IGColumnProps<T extends JOB_TYPE.TRAINING ? ITrainingListItem : IInferenceListItem>[] => {
    const commonColumns: IGColumnProps<T extends JOB_TYPE.TRAINING ? ITrainingListItem : IInferenceListItem>[] = [
      {
        sortBy: 'modelName',
        headerName: t(jobType === JOB_TYPE.TRAINING ? 'name' : 'training_name'),
        renderer: (jobItem: T extends JOB_TYPE.TRAINING ? ITrainingListItem : IInferenceListItem): string | ReactElement => {
          if (jobType === JOB_TYPE.TRAINING) {
            return renderName(jobItem);
          } else {
            return jobItem.trainingName;
          }
        },
      },
      {
        sortBy: 'modelVariant',
        headerName: t('type'),
        renderer: renderVariant,
      },
      {
        sortBy: 'projectName',
        headerName: t('project'),
        renderer: renderProject,
      },
      {
        sortBy: 'dateCreated',
        headerName: t('created_at'),
        renderer: renderCreationDate,
      },

      {
        sortBy: 'score',
        headerName: t('score'),
        renderer: renderScore,
      },
      {
        sortBy: 'costCalculated',
        headerName: t('cost'),
        renderer: renderCost,
      },
      {
        sortBy: 'status',
        headerName: t('status'),
        renderer: renderStatus,
      },
      {
        headerName: t('actions'),
        renderer: renderActions,
        actions: true,
        width: 'min-content',
      },
    ];

    if (jobType === JOB_TYPE.INFERENCE) {
      const castedColumns = commonColumns as IGColumnProps<IInferenceListItem>[];
      castedColumns.unshift({
        sortBy: 'inferenceName',
        headerName: t('name'),
        renderer: renderName,
      });
    }

    if (jobType === JOB_TYPE.TRAINING) {
      const castedColumns = commonColumns as IGColumnProps<ITrainingListItem>[];
      castedColumns.splice(
        4,
        0,
        {
          sortBy: 'trainingTime',
          headerName: t('training_time'),
          renderer: renderTrainingTime,
        },
        {
          sortBy: 'progress',
          headerName: t('progress'),
          renderer: renderProgress,
        }
      );
    }

    return commonColumns;
  };

  return (
    <>
      <S_TabsCard>
        <GTable
          isLoading={isLoading}
          columns={getColumns(jobType)}
          items={jobs}
          paginationProps={pagination}
          onSortChanged={onOrderingChange}
          sortingModel={[sorting]}
          noItemsPlaceholder={isLoading ? '' : t(`${translationPrefix}.list.empty`)}
        />
      </S_TabsCard>
      {downloadData && (
        <JobDownloadModal
          onCancel={cancelDownload}
          onSubmit={submitDownload}
          jobDownload={downloadData}
        />
      )}
    </>
  );
};
